import styled, { css } from 'styles/styled';
import { TextTransform } from 'types';

interface ButtonProps {
  textTransform?: TextTransform;
  type?: string;
  fontWeight?: string;
  width?: string;
  minWidth?: number;
  margin?: boolean;
}

/**
 * Button base styles — aligned with trading-battles-design-system 2 spec
 * (see project/preview/components-buttons.html): 50px tall, 16/700
 * (--tb-fw-heavy), radius 10. Mobile keeps the same proportions so primary
 * and secondary buttons stay the same size next to each other (avoids the
 * legacy 38px shrink that made `ButtonFilled` smaller than its sibling
 * `CancelButton` on payment screens).
 */
const baseStyles = css<ButtonProps>`
  min-width: ${({ minWidth }) => minWidth ?? '64'}px;
  width: ${({ width }) => width ?? 'unset'};
  min-height: 50px;
  padding: 10px 18px;
  font-family: inherit;
  font-weight: ${({ theme, fontWeight }) =>
    fontWeight ?? theme.fontWeight.heavy};
  font-size: 16px;
  letter-spacing: 0.01em;
  cursor: pointer;
  transition: background-color 200ms ease-in, color 200ms ease-in,
    border-color 200ms ease-in, box-shadow 200ms ease-in;
  display: flex;
  align-items: center;
  justify-content: center;
  outline: none;
  margin-right: ${({ margin, theme }) =>
    margin ? `${theme.dimensions.spacingBase16}px` : '0'};
  border-radius: 10px;

  &:disabled {
    cursor: not-allowed;
  }

  @media ${({ theme }) => theme.devices.medium} {
    min-width: ${({ minWidth }) => minWidth ?? '134'}px;
  }
`;

export const ButtonFilled = styled.button<ButtonProps>`
  ${baseStyles};
  background-color: #52D381;
  color: #0A0A0A;
  border: 0;

  &:hover:not(:disabled) {
    background-color: #3FB46A;
  }

  &:focus-visible {
    box-shadow: 0 0 0 4px rgba(82, 211, 129, 0.25);
  }

  &:disabled {
    background-color: #535D70;
    color: #1F2329;
  }

  a {
    color: #0A0A0A;
  }
`;

export const ButtonOutlined = styled.button<ButtonProps>`
  ${baseStyles};
  background-color: transparent;
  color: #52D381;
  border: 1px solid #52D381;

  &:hover:not(:disabled) {
    color: #3FB46A;
    border-color: #3FB46A;
  }

  &:focus-visible {
    box-shadow: 0 0 0 4px rgba(82, 211, 129, 0.25);
  }

  &:disabled {
    color: #535D70;
    border-color: #535D70;
  }
`;
